import { isAxiosError } from "axios";
import axiosInstance from "../../utility/axiosInstance";
import { toast } from "sonner";

export const unsubscribeReminders = async (
  encodedData: string,
  reasons?: string[],
) => {
  try {
    const response = await axiosInstance.post(
      `/organizations/unsubscribe-reminders/${encodedData}`,
      { reasons },
    );

    if (response.data.message) {
      toast.success(response.data.message);
    }

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to unsubscribe";
      toast.error(errorMessage);
    }
    throw error;
  }
};

export const unsubscribeByOrgId = async (encodedData: string) => {
  try {
    const response = await axiosInstance.post(
      `/organizations/unsubscribe-reminders/${encodedData}`,
      // { reasons },
    );

    if (response.data.message) {
      toast.success(response.data.message);
    }

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to unsubscribe";
      toast.error(errorMessage);
    }
    throw error;
  }
};

export interface SlaEmailData {
  tier?: string;
  organizationId?: string;
  frequency?: string;
  featureIds?: string[];
  directPlan?: {
    tier?: string;
    includeSetupFee?: boolean;
  };
  directFeatureIds?: string[];
  amount?: number;
  lifecyclePhaseIds?: string[];
}

export const organizationService = {
  get: async (id: string) => {
    const response = await axiosInstance.get(`/organizations/${id}/slight`);
    return response.data;
  },
  getBookingLink: async (id: string) => {
    const response = await axiosInstance.get(
      `/organizations/${id}/booking-link`,
    );
    return response.data;
  },
  sendSlaEmail: async (id: string, data?: SlaEmailData) => {
    try {
      const response = await axiosInstance.post(
        `/organizations/${id}/sla-email`,
        data,
      );
      if (response.data.message) {
        toast.success(response.data.message);
      }
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to send SLA email";
        toast.error(errorMessage);
      }
      throw error;
    }
  },
  sendInvoiceEmail: async (id: string) => {
    try {
      const response = await axiosInstance.post(
        `/organizations/${id}/send-invoice-email`,
      );
      if (response.data.message) {
        toast.success(response.data.message);
      }
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to send invoice email";
        toast.error(errorMessage);
      }
      throw error;
    }
  },
  approveGoLive: async (role: "ceo" | "cto", organizationId: string) => {
    try {
      const response = await axiosInstance.post(
        `/onboarding/approve-go-live/${role}`,
        { organizationId },
      );
      if (response.data?.message) {
        toast.success(response.data.message);
      }
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          `Failed to approve go-live for ${role.toUpperCase()}`;
        toast.error(errorMessage);
      }
      throw error;
    }
  },
  approveDocument: async (id: string) => {
    try {
      const response = await axiosInstance.post(`/onboarding/approve-docs`, {
        organizationId: id,
      });
      if (response.data.message) {
        toast.success(response.data.message);
      }
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to approve document";
        toast.error(errorMessage);
      }
      throw error;
    }
  },
  // approvePayment: async (id: string) => {
  //   try {
  //     const response = await axiosInstance.post(
  //       `/billing/approve-payment/${id}`,
  //     );
  //     if (response.data.message) {
  //       toast.success(response.data.message);
  //     }
  //     return response.data;
  //   } catch (error) {
  //     if (isAxiosError(error)) {
  //       const errorMessage =
  //         error?.response?.data?.error?.message ||
  //         error?.response?.data?.message ||
  //         "Failed to approve payment";
  //       toast.error(errorMessage);
  //     }
  //     throw error;
  //   }
  // },
  sendTrainingEmail: async (id: string) => {
    try {
      const response = await axiosInstance.post(
        `/organizations/${id}/send-training-email`,
      );
      if (response.data.message) {
        toast.success(response.data.message);
      }
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to send training email";
        toast.error(errorMessage);
      }
      throw error;
    }
  },
  submitAccessDetails: async (id: string, data: any) => {
    try {
      const formData = new FormData();

      // Text / String Fields
      const textKeys = [
        "clientName",
        "dateSubmitted",
        "numberOfPilotCompanies",
        "tierSelected",
        "bundleDiscountApplied",
        "legalCompanyName",
        "legalEntityName",
        "brandDisplayName",
        "headOfficeAddress",
        "industrySector",
        "estimatedEmployeeCount",
        "parentDomain",
        "crossBorderJurisdictions",
        "NIN",
        "TIN",
        "cacRegNumber",
        "totalPhase1Users",
        "roleBasedAccessDetails",
        "thirdPartyAccessDetails",
        "billingContactName",
        "billingContactEmail",
        "billingContactPhone",
        "preferredInvoicingEmail",
        "paymentMethodPreference",
        "specialInvoicingInstructions",
        "existingHRIS",
        "hrisIntegrationDetails",
        "jobPostingPlatforms",
        "emailProvider",
        "smsGatewayProvider",
        "biometricDetails",
        "platformDisplayName",
        "primaryBrandColor",
        "customDomain",
        "complianceRules",
        "additionalDeploymentNotes",
        "jurisdictionDetected",
        "namedUserSeats",
        "additionalSeatsRequired",
        "numberOfMemberEntities",
        "numberOfConcurrentSessions",
        "lifecyclePhase8Custom",
        "billingModel",
        "currency",
        "paymentMethod",
        "targetGoLiveDate",
        "deliveryUrgency",
        "authorizedFullName",
        "authorizedTitle",
        "authorizedDate",
      ];

      textKeys.forEach((key) => {
        let value = data[key];
        if (key === "bundleDiscountApplied" && (value === undefined || value === null || value === "")) {
          value = "0%";
        }
        if (key === "jurisdictionDetected" && (value === undefined || value === null || value === "")) {
          value = "Nigeria";
        }
        if (key === "currency" && (value === undefined || value === null || value === "")) {
          value = "NGN";
        }
        if (key === "deliveryUrgency" && (value === undefined || value === null || value === "")) {
          value = "Standard";
        }

        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });

      // Boolean Fields
      const booleanKeys = [
        "includeEmployeeLifecycle",
        "pilotCompaniesConfirmed",
        "crossBorderProcessing",
        "roleBasedAccessRequired",
        "thirdPartyAccessDeclared",
        "hrisIntegrationRequired",
        "biometricIntegrationRequired",
        "multiEntityFlag",
        "concurrentLicensingRequired",
        "lifecyclePhase1",
        "trainingCompletionRecord",
        "uatSignOff",
        "dataMigrationVerified",
        "integrationsTested",
        "clientL0GoLiveReadinessDeclaration",
        "scopeConfirmed",
        "expansionAcknowledged",
        "unauthorizedBreachAcknowledged",
        "dataComplianceConfirmed",
        "electronicAgreement",
      ];

      booleanKeys.forEach((key) => {
        if (data[key] !== undefined && data[key] !== null) {
          formData.append(key, data[key] ? "true" : "false");
        }
      });

      // JSON Fields
      const jsonKeys = [
        "productsSelected",
        "l0Contact",
        "financeContact",
        "projectManagerContact",
        "lifecyclePhases2to7",
      ];

      jsonKeys.forEach((key) => {
        if (data[key] !== undefined && data[key] !== null) {
          formData.append(key, JSON.stringify(data[key]));
        }
      });

      // Nested Arrays (send as JSON strings)
      const nestedArrayKeys = ["personnel", "childOrganizations"];
      nestedArrayKeys.forEach((key) => {
        if (data[key] !== undefined && data[key] !== null) {
          formData.append(key, JSON.stringify(data[key]));
        }
      });

      // File Uploads
      const file = data.cacDoc || data.cacFile;
      if (file) {
        formData.append("cacDoc", file);
      }

      const response = await axiosInstance.post(
        `/organizations/${id}/access-details`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to submit access details";
        toast.error(errorMessage);
      }
      throw error;
    }
  },
  enqueueProgressReport: async (
    id: string,
    deadline: string,
    reportFile: File,
  ) => {
    try {
      const formData = new FormData();
      formData.append("deadline", deadline);
      formData.append("report", reportFile);

      const response = await axiosInstance.patch(
        `/organizations/${id}/onboarding-deadline`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (response.data.message) {
        toast.success(response.data.message);
      }

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to enqueue progress report";
        toast.error(errorMessage);
      }
      throw error;
    }
  },
  sendPostOnboardDocuments: async (id: string) => {
    try {
      const response = await axiosInstance.post(
        `/organizations/${id}/send-post-onboard-documents`
      );
      if (response.data.message) {
        toast.success(response.data.message);
      }
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to send post-onboard agreement pack";
        toast.error(errorMessage);
      }
      throw error;
    }
  },
  sendBulkPostOnboardDocuments: async () => {
    try {
      const response = await axiosInstance.post(
        "/organizations/send-bulk-post-onboard-documents"
      );
      if (response.data.message) {
        toast.success(response.data.message);
      }
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to send bulk post-onboard agreements";
        toast.error(errorMessage);
      }
      throw error;
    }
  },
  acceptPostOnboardDocuments: async (id: string) => {
    try {
      const response = await axiosInstance.post(
        `/organizations/${id}/accept-post-onboard-documents`
      );
      if (response.data.message) {
        toast.success(response.data.message);
      }
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to accept post-onboard agreements";
        toast.error(errorMessage);
      }
      throw error;
    }
  },
};

